/**
 * Service para gerenciar configurações de transcrição
 */

import type { UIConfig, AiFileTranscriptionSettings } from '../types/settings'

const API_BASE_URL = import.meta.env.VITE_AI_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8087/transcript'

/**
 * Normaliza URL removendo barras duplicadas
 */
function normalizeUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const endpoint = path.startsWith('/') ? path : `/${path}`
  return `${base}${endpoint}`.replace(/([^:]\/)\/+/g, '$1')
}

export interface SettingsServiceConfig {
  tennant: string
  player?: string
  apiBaseUrl?: string
}

/**
 * Busca configurações completas de transcrição
 */
export async function getSettings(
  tennant: string,
  player?: string,
  apiBaseUrl?: string
): Promise<AiFileTranscriptionSettings> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  const params = new URLSearchParams({ tennant })
  if (player) params.set('player', player)
  
  const response = await fetch(
    normalizeUrl(baseUrl, `/api/v1/transcription-settings?${params}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to fetch settings: ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Busca apenas configurações de UI (otimizado)
 */
export async function getUIConfig(
  tennant: string,
  player?: string,
  apiBaseUrl?: string
): Promise<UIConfig> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  const params = new URLSearchParams({ tennant })
  if (player) params.set('player', player)
  
  const response = await fetch(
    normalizeUrl(baseUrl, `/api/v1/transcription-settings/ui-config?${params}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to fetch UI config: ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Cria nova configuração
 */
export async function createSettings(
  settings: Partial<AiFileTranscriptionSettings>,
  apiBaseUrl?: string
): Promise<AiFileTranscriptionSettings> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  
  const response = await fetch(
    normalizeUrl(baseUrl, '/api/v1/transcription-settings'),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to create settings: ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Atualiza configuração existente
 */
export async function updateSettings(
  id: string,
  settings: Partial<AiFileTranscriptionSettings>,
  apiBaseUrl?: string
): Promise<AiFileTranscriptionSettings> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  
  const response = await fetch(
    normalizeUrl(baseUrl, `/api/v1/transcription-settings/${id}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to update settings: ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Desativa configuração (soft delete)
 */
export async function deleteSettings(
  id: string,
  apiBaseUrl?: string
): Promise<void> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  
  const response = await fetch(
    normalizeUrl(baseUrl, `/api/v1/transcription-settings/${id}`),
    {
      method: 'DELETE',
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to delete settings: ${response.statusText}`)
  }
}

/**
 * Limpa cache de configurações
 */
export async function clearSettingsCache(
  tennant?: string,
  player?: string,
  apiBaseUrl?: string
): Promise<void> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  const params = new URLSearchParams()
  if (tennant) params.set('tennant', tennant)
  if (player) params.set('player', player)
  
  const queryString = params.toString()
  const path = queryString
    ? `/api/v1/transcription-settings/cache/clear?${queryString}`
    : '/api/v1/transcription-settings/cache/clear'
  
  const response = await fetch(normalizeUrl(baseUrl, path), {
    method: 'POST',
  })
  
  if (!response.ok) {
    throw new Error(`Failed to clear cache: ${response.statusText}`)
  }
}

/**
 * Inicializa configuração DEFAULT global
 */
export async function initDefaultSettings(
  apiBaseUrl?: string
): Promise<AiFileTranscriptionSettings> {
  const baseUrl = apiBaseUrl || API_BASE_URL
  
  const response = await fetch(
    normalizeUrl(baseUrl, '/api/v1/transcription-settings/init-default'),
    {
      method: 'POST',
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to init default settings: ${response.statusText}`)
  }
  
  return response.json()
}
